package one.devsbox.www.ui.company.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.DeleteOutline
import compose.assets.svg.EditOutline
import kotlinx.browser.window
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.navTo_CompanyEUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class CompanyDVM : NavigationViewModel() {
    private lateinit var ctx: CompanyDCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    data class Data(val company: SharedIO.CompanySimple)

    var state by mutableStateOf<DataState<Data>>(DataState.Loading())
        private set

    var showDeleteConfirmation by mutableStateOf(false)
    var showDeleteError by mutableStateOf(false)
    var deleteErrorMessage by mutableStateOf("")

    interface Action {
        fun edit(id: Long)
        fun delete()
        fun confirmDelete()
        fun dismissDeleteConfirmation()
        fun dismissDeleteError()
        fun switchCompany()
        fun back()
        fun reload()
    }

    val action = object : Action {
        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadCompany(ctx.args.id)
        }

        override fun edit(id: Long) {
            ctx.nav.navTo_CompanyEUI(ArgsId(id))
        }

        override fun delete() {
            showDeleteConfirmation = true
        }

        override fun dismissDeleteConfirmation() {
            showDeleteConfirmation = false
        }

        override fun dismissDeleteError() {
            showDeleteError = false
        }

        override fun confirmDelete() {
            showDeleteConfirmation = false
            state = DataState.Loading(state.entry)
            scope.launch {
                runCatching {
                    Repository.ads.company.delete(ctx.args.id)
                }.onFailure {
                    it.printStackTrace()
                    deleteErrorMessage = it.message ?: "Failed to delete company"
                    showDeleteError = true
                    state = DataState.Result(state.entry!!)
                }.onSuccess {
                    ctx.nav.up()
                }
            }
        }

        override fun switchCompany() {
            val company = state.entry?.company ?: return
            scope.launch {
                runCatching {
                    Repository.ads.user.switchCompany(company.id)
                }.onSuccess {
                    window.location.reload()
                }.onFailure {
                    it.printStackTrace()
                }
            }
        }
    }

    fun setup(ctx: CompanyDCtx) {
        this.ctx = ctx
        loadCompany(ctx.args.id)
    }

    private fun loadCompany(id: Long) {
        state = DataState.Loading()
        scope.launch {
            runCatching {
                Repository.ads.company.get(id)
            }.onSuccess {
                state = DataState.Result(Data(it))
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
            }
            setupActions(state)
        }
    }

    private fun setupActions(result: DataState<Data>) {
        contentActions = result.entry?.company?.run {
            buildList {
                if (ctx.me.permission.company.write) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_locations_detail_btn_edit,
                            color = ctx.colorSecondaryContainer,
                            icon = Svg.EditOutline
                        ) {
                            action.edit(ctx.args.id)
                        }
                    )
                }
                if (ctx.me.permission.company.delete && !ctx.me.info.adminMode) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_company_dialog_btn_delete,
                            color = ctx.colorErrorContainer,
                            icon = Svg.DeleteOutline
                        ) {
                            action.delete()
                        }
                    )
                }
            }
        } ?: emptyList()
    }

    override fun onRelease() {}
}
